import { LAYOUT } from './ruleset';
import type { Lane } from './types';

export interface LaneSample {
  x: number;
  y: number;
  /** Unit tangent, from the player's fortress toward the enemy's. */
  tx: number;
  ty: number;
  /** Unit normal, to the left of that tangent. */
  nx: number;
  ny: number;
}

interface Pt {
  x: number;
  y: number;
}

/**
 * Left lane, walked from the player's fortress (bottom) to the enemy's (top).
 * A straight flank beside both turret grids. The right lane is the mirror.
 */
function leftPoints(): Pt[] {
  const flank = LAYOUT.lanes.x[0];
  return [
    { x: flank, y: LAYOUT.lanes.bottomY },
    { x: flank, y: LAYOUT.lanes.topY },
  ];
}

function mirror(points: Pt[]): Pt[] {
  return points.map((p) => ({ x: LAYOUT.width - p.x, y: p.y }));
}

interface Seg {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  len: number;
  tx: number;
  ty: number;
}

function segments(points: Pt[]): { segs: Seg[]; length: number } {
  const segs: Seg[] = [];
  let length = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    segs.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, len, tx: dx / len, ty: dy / len });
    length += len;
  }
  return { segs, length };
}

const LEFT = segments(leftPoints());
const RIGHT = segments(mirror(leftPoints()));
const LANES: [typeof LEFT, typeof LEFT] = [LEFT, RIGHT];

export const LANE_LENGTH = LEFT.length;
/** Content speeds were tuned on a 300px straight lane. This keeps the walk readable on the longer road. */
export const LANE_SPEED_SCALE = LANE_LENGTH / 500;

export function lanePoints(lane: Lane): Pt[] {
  return lane === 0 ? leftPoints() : mirror(leftPoints());
}

export function laneSample(lane: Lane, distance: number): LaneSample {
  const { segs, length } = LANES[lane];
  let d = distance;
  if (d <= 0) d = 0;
  if (d >= length) d = length - 0.001;
  for (const s of segs) {
    if (d <= s.len) {
      const k = s.len === 0 ? 0 : d / s.len;
      return {
        x: s.ax + (s.bx - s.ax) * k,
        y: s.ay + (s.by - s.ay) * k,
        tx: s.tx,
        ty: s.ty,
        nx: -s.ty,
        ny: s.tx,
      };
    }
    d -= s.len;
  }
  const s = segs[segs.length - 1];
  return { x: s.bx, y: s.by, tx: s.tx, ty: s.ty, nx: -s.ty, ny: s.tx };
}
